import { useEffect, useState } from "react";
import { TrendingUp, AlertCircle, Loader2, ExternalLink, Globe, Users, Zap, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import api, { fmtINR, fmtNum } from "@/lib/api";

const WINDOWS = [{ d: 1, label: "24h" }, { d: 7, label: "7d" }, { d: 30, label: "30d" }, { d: 90, label: "90d" }];

/**
 * Per-link earnings dashboard — used by both partner (own) and admin
 * (per-partner). Pass `path` (the GET endpoint) and optional `lockedHint`
 * shown when the API returns 402 (analytics tier not enabled).
 */
export default function PerLinkAnalytics({ path, scope = "partner", upgradeCta }) {
    const [days, setDays] = useState(7);
    const [data, setData] = useState(null);
    const [locked, setLocked] = useState(false);
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true); setErr(""); setLocked(false);
            try {
                const r = await api.get(`${path}?days=${days}`);
                if (alive) setData(r.data);
            } catch (e) {
                if (e?.response?.status === 402) { setLocked(true); }
                else { setErr(e?.response?.data?.detail || e.message); }
            } finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [path, days]);

    if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline-block text-blue-600" /></div>;

    if (locked) return (
        <div className="card p-12 text-center max-w-2xl mx-auto" data-testid="analytics-locked">
            <Lock className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold">Analytics Pro is not active</h2>
            <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
                {upgradeCta || "Per-link earnings, conversion rate, top customers and UPI app breakdown are available on the Pro tier. Contact your account manager to enable it."}
            </p>
        </div>
    );

    if (err) return <div className="card p-6 text-rose-600 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{err}</div>;
    if (!data) return null;

    const { totals, links } = data;
    return (
        <div className="space-y-5" data-testid="per-link-analytics">
            <div className="flex items-center gap-2 flex-wrap">
                <div className="text-xs uppercase tracking-wider text-slate-500">Window:</div>
                {WINDOWS.map((w) => (
                    <button key={w.d} data-testid={`window-${w.d}`}
                            onClick={() => setDays(w.d)}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition ${
                                days === w.d ? "bg-slate-900 text-white" : "bg-slate-100 hover:bg-slate-200"
                            }`}>
                        {w.label}
                    </button>
                ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Stat label="Volume" value={fmtINR(totals.success_volume)} accent="emerald" icon={TrendingUp} />
                <Stat label="Successes" value={fmtNum(totals.success_count)} icon={Zap} />
                <Stat label="Failures" value={fmtNum(totals.fail_count)} accent="rose" />
                <Stat label="Conversion" value={`${totals.conversion_rate}%`}
                      accent={totals.conversion_rate > 70 ? "emerald" : totals.conversion_rate > 40 ? "amber" : "rose"} />
            </div>

            <div>
                <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-2">By payment link</h2>
                {links.length === 0 ? (
                    <div className="card p-6 text-center text-slate-400 text-sm">No transactions in this window yet.</div>
                ) : (
                    <div className="space-y-3">
                        {links.map((l) => <LinkAnalyticsCard key={l.link_code} link={l} scope={scope} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

function Stat({ label, value, accent, icon: Icon }) {
    const bg = accent === "emerald" ? "bg-emerald-50 text-emerald-700"
             : accent === "rose" ? "bg-rose-50 text-rose-700"
             : accent === "amber" ? "bg-amber-50 text-amber-700"
             : "bg-slate-100 text-slate-700";
    return (
        <div className="card p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-500 mb-1.5">
                {Icon && <Icon className="w-3.5 h-3.5" />} {label}
            </div>
            <div className={`inline-block px-2.5 py-1 rounded-md text-2xl font-bold font-mono ${bg}`}>{value}</div>
        </div>
    );
}

function LinkAnalyticsCard({ link, scope }) {
    const liveUrl = `${window.location.origin}/pay/${link.link_code}`;
    return (
        <div data-testid={`analytics-link-${link.link_code}`}
             className="card p-4 hover:shadow-md transition">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-base">{link.display_name}</span>
                        <span className={`pill ${link.status === "active" ? "pill-success" : "pill-failed"}`}>{link.status}</span>
                        {link.custom_domain && link.domain_status === "dns_verified" && (
                            <span className="pill pill-success flex items-center gap-1"><Globe className="w-3 h-3" /> domain live</span>
                        )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 font-mono flex items-center gap-2 flex-wrap">
                        <a href={liveUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                            /pay/{link.link_code} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                        {link.custom_domain && <span className="text-emerald-700">· {link.custom_domain}</span>}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Volume</div>
                    <div className="font-mono font-bold text-lg text-emerald-700">{fmtINR(link.success_volume)}</div>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 text-xs">
                <Mini label="Transactions" value={fmtNum(link.txn_count)} sub={`${link.success_count} success · ${link.fail_count} failed`} />
                <Mini label="Conversion" value={`${link.conversion_rate}%`}
                      sub={link.tap_to_success_rate != null ? `Tap→Success ${link.tap_to_success_rate}%` : "—"}
                      accent={link.conversion_rate > 70 ? "emerald" : link.conversion_rate > 40 ? "amber" : "rose"} />
                <Mini label="Avg deposit" value={fmtINR(link.avg_deposit)} sub={link.pending_count ? `${link.pending_count} pending` : "—"} />
                <Mini label="Top UPI app"
                      value={link.top_upi_app ? link.top_upi_app.toUpperCase() : "—"}
                      sub={link.top_upi_app ? `${fmtNum(link.top_upi_app_taps)} taps` : "no taps"} />
                {/* QR-shared cohort: txns where the customer at least once
                    tapped Share/Download on the QR. Strong proxy for "paid by
                    a friend/family member on the customer's behalf" — a
                    cohort that's otherwise invisible to the partner. */}
                <Mini label="QR shared"
                      value={fmtNum(link.qr_share_count || 0)}
                      sub={link.qr_share_to_success_rate != null
                          ? `→ ${link.qr_share_to_success_rate}% success`
                          : (link.qr_share_count ? "no successes yet" : "no shares")}
                      accent={link.qr_share_to_success_rate != null
                          ? (link.qr_share_to_success_rate >= (link.conversion_rate || 0) ? "emerald" : "amber")
                          : null} />
            </div>

            {/* Time-to-success comparison — only render when we have at least
                one sample on each side (otherwise the comparison is meaningless
                and would look like a bug to the partner). The widget tells
                partners whether shared QRs convert dramatically slower (typical
                pattern: 3-5×, because the friend has to be reached on WhatsApp
                first) — strong signal for enabling auto-extend-on-share. */}
            {link.shared_t2s_sample > 0 && link.unshared_t2s_sample > 0 && (
                <T2SCompare link={link} />
            )}

            {link.top_customer_username && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-600">
                    <div className="flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-slate-400" />
                        Top customer: <strong>{link.top_customer_username}</strong>
                    </div>
                    <div className="font-mono">
                        {fmtINR(link.top_customer_volume)} · {link.top_customer_deposits} deposit{link.top_customer_deposits === 1 ? "" : "s"}
                    </div>
                </div>
            )}
        </div>
    );
}

function Mini({ label, value, sub, accent }) {
    const accentCls = accent === "emerald" ? "text-emerald-700"
                    : accent === "rose" ? "text-rose-700"
                    : accent === "amber" ? "text-amber-700"
                    : "text-slate-900";
    return (
        <div className="bg-slate-50/50 rounded p-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
            <div className={`font-mono font-bold mt-0.5 ${accentCls}`}>{value}</div>
            <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>
        </div>
    );
}

/** Format a seconds duration into a compact human-readable label. */
function fmtDur(seconds) {
    if (seconds == null || !Number.isFinite(seconds)) return "—";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
}

/**
 * Time-to-success comparison — surfaces whether shared QRs convert
 * dramatically slower than self-paid ones. Pre-computed by the analytics
 * service using medians (more stable than means against the long-tail
 * of customers who paid 23 hours after creation).
 */
function T2SCompare({ link }) {
    const shared = link.shared_avg_seconds_to_success;
    const unshared = link.unshared_avg_seconds_to_success;
    if (shared == null || unshared == null || unshared === 0) return null;
    const ratio = shared / unshared;
    const isSlower = ratio >= 1.5; // anything below 1.5× isn't worth flagging
    const ratioLabel = ratio >= 1 ? `${ratio.toFixed(1)}× slower` : `${(1 / ratio).toFixed(1)}× faster`;
    return (
        <div data-testid={`t2s-compare-${link.link_code}`}
             className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2 text-xs">
            <div className="flex items-center gap-2 text-slate-600">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">Time-to-success</div>
                <span className="font-mono">
                    <span className="text-slate-500">self-paid </span>
                    <span className="font-semibold text-slate-800">{fmtDur(unshared)}</span>
                    <span className="text-slate-400 mx-1.5">vs</span>
                    <span className="text-slate-500">shared </span>
                    <span className="font-semibold text-slate-800">{fmtDur(shared)}</span>
                </span>
            </div>
            <div className={`pill ${isSlower ? "pill-failed" : "pill-success"} text-[10px] font-mono`}>
                {ratioLabel}
            </div>
            {isSlower && (
                <div className="basis-full text-[10px] text-amber-700 italic">
                    💡 Shared QRs take significantly longer to convert. Consider enabling
                    <span className="font-semibold"> auto-extend session timer on share</span> in the link settings to reduce shared-cohort expiry.
                </div>
            )}
        </div>
    );
}
